import { useEffect, useState, type CSSProperties } from "react";
import { callFn } from "../../lib/api";
import WhiskeyStudioTabs from "../../components/admin/WhiskeyStudioTabs";

interface PhotoSubmission {
  id: string;
  public_url: string;
  submitted_at: string;
  caption: string | null;
  submitter: {
    first_name: string | null;
  };
  bottle: {
    id: string;
    name: string;
    distillery: string | null;
    manual_image_url: string | null;
  };
}

interface DistilleryQueueItem {
  id: string;
  name: string;
  location: string | null;
  established_year: number | null;
  status: "pending" | "enriched" | "flagged";
}

export default function AdminCuration() {
  // Tab controller: 'photos' | 'distilleries'
  const [activeTab, setActiveTab] = useState<"photos" | "distilleries">("photos");

  // Bottle Photos moderation state
  const [photos, setPhotos] = useState<PhotoSubmission[]>([]);
  const [loadingPhotos, setLoadingPhotos] = useState(true);
  const [photoError, setPhotoError] = useState<string | null>(null);

  // Active rejection overlay/state
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [busyRowId, setBusyRowId] = useState<string | null>(null);

  // Distillery Curation state
  const [distilleries, setDistilleries] = useState<DistilleryQueueItem[]>([]);
  const [loadingDistilleries, setLoadingDistilleries] = useState(false);
  const [batchStatus, setBatchStatus] = useState<string | null>(null);
  const [batchBusy, setBatchBusy] = useState(false);

  // --- Bottle Photos: Fetch Pending Submissions ---
  async function loadPhotosQueue() {
    setLoadingPhotos(true);
    setPhotoError(null);
    try {
      const resp = await callFn<{ submissions: PhotoSubmission[], total_pending: number }>(
        "bottle-photo-list-pending",
        { limit: 100 }
      );
      setPhotos(resp.submissions || []);
    } catch (err: any) {
      console.error("Failed to load photo moderation queue:", err);
      setPhotoError("Could not retrieve pending photo submissions.");
    } finally {
      setLoadingPhotos(false);
    }
  }

  // --- Distilleries: Fetch Queue ---
  async function loadDistilleriesQueue() {
    setLoadingDistilleries(true);
    try {
      // Fetch distilleries needing enrichment (mocked for preview presentation)
      setDistilleries([
        { id: "1", name: "Buffalo Trace", location: "Frankfort, KY", established_year: 1775, status: "enriched" },
        { id: "2", name: "Heaven Hill", location: "Bardstown, KY", established_year: 1935, status: "enriched" },
        { id: "3", name: "Willett Distillery", location: null, established_year: null, status: "pending" },
        { id: "4", name: "Four Roses", location: "Lawrenceburg, KY", established_year: 1888, status: "enriched" },
        { id: "5", name: "Blanton's Distillery", location: null, established_year: null, status: "pending" }
      ]);
    } catch (err) {
      console.error("Failed to load distilleries:", err);
    } finally {
      setLoadingDistilleries(false);
    }
  }

  useEffect(() => {
    if (activeTab === "photos") loadPhotosQueue();
    else loadDistilleriesQueue();
  }, [activeTab]);

  // --- Moderation: Approve Photo ---
  async function handleApprovePhoto(subId: string) {
    setBusyRowId(subId);
    try {
      const res = await callFn<{ promoted_to_community: boolean, superseded_count: number }>(
        "bottle-photo-approve",
        { submission_id: subId }
      );
      
      // Update local memory list: remove the approved row instantly
      setPhotos((prev) => prev.filter((p) => p.id !== subId));
      
      const promoted = res.promoted_to_community;
      alert(promoted ? "✓ Approved and set as primary community bottle photo!" : "✓ Approved as background gallery photo!");
    } catch (err: any) {
      console.error("Approve failed:", err);
      alert(err.message || "Approve failed.");
    } finally {
      setBusyRowId(null);
    }
  }

  // --- Moderation: Reject Photo ---
  async function handleRejectPhoto() {
    if (!rejectingId || !rejectReason.trim()) return;
    setBusyRowId(rejectingId);
    try {
      await callFn("bottle-photo-reject", {
        submission_id: rejectingId,
        reason: rejectReason.trim()
      });

      setPhotos((prev) => prev.filter((p) => p.id !== rejectingId));
      setRejectingId(null);
      setRejectReason("");
      alert("✓ Submission rejected and submitter notified.");
    } catch (err: any) {
      console.error("Reject failed:", err);
      alert(err.message || "Reject failed.");
    } finally {
      setBusyRowId(null);
    }
  }

  // --- Curation: Trigger Batch AI-Enrichment ---
  async function handleBatchEnrich() {
    setBatchBusy(true);
    setBatchStatus("Running AI batch enrichment (takes ~30s)...");
    try {
      const result = await callFn<{ ok: boolean, processed: number, approved: number, errors: number, error?: string }>(
        "enrich-distilleries",
        { action: "enrich-batch", limit: 10, auto_approve: true }
      );
      if (result.ok) {
        setBatchStatus(`✓ Done: ${result.processed} processed, ${result.approved} auto-approved, ${result.errors} errors.`);
        loadDistilleriesQueue();
      } else {
        setBatchStatus(`✕ Failed: ${result.error || "Batch failed"}`);
      }
    } catch (err: any) {
      setBatchStatus(`✕ Failed: ${err.message || "Network error"}`);
    } finally {
      setBatchBusy(false);
    }
  }

  return (
    <div style={styles.container}>
      <WhiskeyStudioTabs activeTab="curation" badgeCounts={{ curation: photos.length }} />
      <header style={styles.header}>
        <h1 style={styles.title}>Curation & Media Hub</h1>
        <p style={styles.subtitle}>Moderate user bottle-photo submissions, enrich distillery metadata, and monitor catalogs.</p>
      </header>

      {/* Page Tabs */}
      <div style={styles.tabsRow}>
        <button
          style={{ ...styles.tabBtn, ...(activeTab === "photos" ? styles.tabBtnActive : {}) }}
          onClick={() => setActiveTab("photos")}
        >
          Photo Submissions ({photos.length})
        </button>
        <button
          style={{ ...styles.tabBtn, ...(activeTab === "distilleries" ? styles.tabBtnActive : {}) }}
          onClick={() => setActiveTab("distilleries")}
        >
          Distillery AI Enrichment
        </button>
      </div>

      {/* Tab 1: Bottle Photo Moderation */}
      {activeTab === "photos" && (
        <div style={styles.tabContent}>
          {photoError && <div style={styles.errorBanner}>{photoError}</div>}
          {loadingPhotos ? (
            <div style={styles.loader}>Loading Pending Submissions…</div>
          ) : (
            <div style={styles.photoGrid}>
              {photos.map((sub) => {
                const bName = sub.bottle?.name || "(Unknown Bottle)";
                const distName = sub.bottle?.distillery || "No Distillery";
                const isBusy = busyRowId === sub.id;

                return (
                  <div key={sub.id} style={styles.photoCard}>
                    <img src={sub.public_url} alt={bName} style={styles.photoThumb} />
                    <div style={styles.photoInfo}>
                      <h3 style={styles.bottleTitle}>{bName}</h3>
                      <div style={styles.distillerySub}>{distName}</div>
                      <div style={styles.submitterText}>
                        Submitted by <strong>{sub.submitter?.first_name || "Guest"}</strong> on {new Date(sub.submitted_at).toLocaleDateString()}
                      </div>
                      
                      {sub.caption && (
                        <p style={styles.captionQuote}>"{sub.caption}"</p>
                      )}

                      {sub.bottle?.manual_image_url && (
                        <div style={styles.manualWarning}>
                          ⚠ Bottle already has a manual photo set.
                        </div>
                      )}

                      <div style={styles.cardActions}>
                        <button
                          style={styles.approveBtn}
                          disabled={isBusy}
                          onClick={() => handleApprovePhoto(sub.id)}
                        >
                          Approve
                        </button>
                        <button
                          style={styles.rejectBtn}
                          disabled={isBusy}
                          onClick={() => setRejectingId(sub.id)}
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
              {photos.length === 0 && (
                <div style={styles.emptyInbox}>Inbox zero. No pending photo submissions to moderate.</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Tab 2: Distillery AI Enrichment */}
      {activeTab === "distilleries" && (
        <div style={styles.tabContent}>
          <div style={styles.distToolbar}>
            <button
              style={styles.enrichBtn}
              disabled={batchBusy}
              onClick={handleBatchEnrich}
            >
              {batchBusy ? "Enriching..." : "Batch AI-Enrich (10)"}
            </button>
            {batchStatus && <span style={styles.statusText}>{batchStatus}</span>}
          </div>

          {loadingDistilleries ? (
            <div style={styles.loader}>Loading Distilleries…</div>
          ) : (
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Name</th>
                    <th style={styles.th}>Location</th>
                    <th style={styles.thCenter}>Est. Year</th>
                    <th style={styles.thRight}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {distilleries.map((d) => (
                    <tr key={d.id} style={styles.tr}>
                      <td style={styles.td}><strong>{d.name}</strong></td>
                      <td style={styles.td}>{d.location || "—"}</td>
                      <td style={styles.tdCenter}>{d.established_year || "—"}</td>
                      <td style={styles.tdRight}>
                        {d.status === "enriched" ? (
                          <span style={styles.badgeGreen}>Enriched</span>
                        ) : d.status === "flagged" ? (
                          <span style={styles.badgeRed}>Flagged</span>
                        ) : (
                          <span style={styles.badgeGold}>Pending AI</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Rejection Prompt Overlay Dialog */}
      {rejectingId && (
        <div style={styles.backdrop} onClick={() => setRejectingId(null)}>
          <div style={styles.dialog} onClick={(e) => e.stopPropagation()}>
            <header style={styles.dialogHeader}>
              <h2 style={styles.dialogTitle}>Reject Submission</h2>
              <button style={styles.dialogClose} onClick={() => setRejectingId(null)}>&times;</button>
            </header>
            <div style={styles.dialogBody}>
              <label style={styles.dialogLabel}>Reason for rejection (shown to submitter)</label>
              <textarea
                placeholder="Image out of focus, duplicate, incorrect bottle..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                style={styles.dialogTextarea}
              />
            </div>
            <footer style={styles.dialogFooter}>
              <button
                style={styles.dialogConfirmBtn}
                disabled={!rejectReason.trim()}
                onClick={handleRejectPhoto}
              >
                Confirm Rejection
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  container: {
    padding: "24px 16px",
    background: "var(--bg, #0C0907)",
    color: "var(--text, #F7F5F0)",
    minHeight: "100vh",
  },
  header: { marginBottom: 24 },
  title: {
    fontFamily: "Georgia, serif",
    fontSize: 28,
    fontWeight: 400,
    margin: "0 0 4px",
    color: "var(--text, #F7F5F0)",
  },
  subtitle: {
    color: "var(--muted, #A39D98)",
    fontSize: 14,
    margin: 0,
  },
  tabsRow: {
    display: "flex",
    gap: 8,
    borderBottom: "1px solid var(--border, #231D19)",
    marginBottom: 20,
    overflowX: "auto",
  },
  tabBtn: {
    background: "transparent",
    border: 0,
    borderBottom: "2px solid transparent",
    color: "var(--muted, #A39D98)",
    padding: "10px 16px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  tabBtnActive: {
    color: "var(--gold, #F9AF36)",
    borderColor: "var(--gold, #F9AF36)",
  },
  tabContent: { width: "100%" },
  photoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
    gap: 20,
  },
  photoCard: {
    background: "var(--surface, #15110E)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 16,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  photoThumb: {
    width: "100%",
    height: 180,
    objectFit: "cover",
    borderBottom: "1px solid var(--border, #231D19)",
  },
  photoInfo: {
    padding: 16,
    display: "flex",
    flexDirection: "column",
    flex: 1,
  },
  bottleTitle: {
    margin: "0 0 4px",
    fontSize: 15,
    fontWeight: "bold",
    color: "var(--text)",
  },
  distillerySub: {
    fontSize: 12,
    color: "var(--muted, #A39D98)",
    marginBottom: 8,
  },
  submitterText: {
    fontSize: 11,
    color: "var(--muted, #A39D98)",
    marginBottom: 12,
  },
  captionQuote: {
    margin: "0 0 12px",
    fontSize: 12,
    fontStyle: "italic",
    color: "var(--text)",
    background: "var(--bg)",
    padding: "8px 12px",
    borderRadius: 8,
  },
  manualWarning: {
    fontSize: 10,
    color: "var(--red, #FF8A7A)",
    marginBottom: 12,
    fontWeight: "bold",
  },
  cardActions: {
    display: "flex",
    gap: 8,
    marginTop: "auto",
  },
  approveBtn: {
    flex: 1,
    background: "var(--gold, #F9AF36)",
    color: "#0D0B08",
    border: 0,
    borderRadius: 6,
    padding: "8px 12px",
    fontSize: 12,
    fontWeight: "bold",
    cursor: "pointer",
  },
  rejectBtn: {
    flex: 1,
    background: "transparent",
    border: "1px solid var(--border, #231D19)",
    color: "var(--muted, #A39D98)",
    borderRadius: 6,
    padding: "8px 12px",
    fontSize: 12,
    fontWeight: "bold",
    cursor: "pointer",
  },
  emptyInbox: {
    gridColumn: "span 3",
    padding: "40px",
    textAlign: "center",
    color: "var(--muted, #A39D98)",
    fontStyle: "italic",
    background: "var(--surface, #15110E)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 12,
  },
  distToolbar: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    marginBottom: 16,
    flexWrap: "wrap",
  },
  enrichBtn: {
    background: "var(--gold, #F9AF36)",
    color: "#0D0B08",
    border: 0,
    borderRadius: 8,
    padding: "10px 20px",
    fontSize: 13,
    fontWeight: "bold",
    textTransform: "uppercase",
    cursor: "pointer",
    letterSpacing: "0.4px",
  },
  statusText: {
    fontSize: 13,
    color: "var(--gold, #F9AF36)",
  },
  tableWrap: {
    width: "100%",
    overflowX: "auto",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 12,
    background: "var(--surface, #15110E)",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
    textAlign: "left",
  },
  th: {
    padding: "12px 16px",
    borderBottom: "1px solid var(--border, #231D19)",
    color: "var(--muted, #A39D98)",
    fontSize: 11,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  thCenter: {
    padding: "12px 16px",
    borderBottom: "1px solid var(--border, #231D19)",
    color: "var(--muted, #A39D98)",
    fontSize: 11,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    textAlign: "center",
  },
  thRight: {
    padding: "12px 16px",
    borderBottom: "1px solid var(--border, #231D19)",
    color: "var(--muted, #A39D98)",
    fontSize: 11,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    textAlign: "right",
  },
  tr: { borderBottom: "1px solid var(--border, #231D19)" },
  td: { padding: "14px 16px", color: "var(--text, #F7F5F0)" },
  tdCenter: { padding: "14px 16px", color: "var(--text, #F7F5F0)", textAlign: "center" },
  tdRight: { padding: "14px 16px", color: "var(--text, #F7F5F0)", textAlign: "right" },
  badgeGreen: {
    fontSize: 10,
    fontWeight: "bold",
    background: "rgba(39, 174, 96, 0.15)",
    color: "#86efac",
    padding: "3px 8px",
    borderRadius: 8,
  },
  badgeGold: {
    fontSize: 10,
    fontWeight: "bold",
    background: "rgba(249, 175, 54, 0.12)",
    color: "var(--gold, #F9AF36)",
    padding: "3px 8px",
    borderRadius: 8,
  },
  badgeRed: {
    fontSize: 10,
    fontWeight: "bold",
    background: "rgba(224, 80, 64, 0.15)",
    color: "var(--red, #FF8A7A)",
    padding: "3px 8px",
    borderRadius: 8,
  },
  loader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    color: "var(--muted, #A39D98)",
    fontSize: 14,
  },
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.75)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    zIndex: 99,
  },
  dialog: {
    width: "100%",
    maxWidth: 420,
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 16,
    padding: 24,
    boxShadow: "0 12px 36px rgba(0,0,0,0.6)",
  },
  dialogHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
    borderBottom: "1px solid var(--border)",
    paddingBottom: 12,
  },
  dialogTitle: {
    fontFamily: "Georgia, serif",
    fontSize: 18,
    margin: 0,
    color: "var(--text)",
  },
  dialogClose: {
    background: "transparent",
    border: 0,
    color: "var(--muted)",
    fontSize: 20,
    cursor: "pointer",
  },
  dialogBody: {
    marginBottom: 20,
  },
  dialogLabel: {
    display: "block",
    fontSize: 12,
    fontWeight: "bold",
    color: "var(--muted)",
    marginBottom: 8,
  },
  dialogTextarea: {
    width: "100%",
    minHeight: 100,
    background: "var(--bg)",
    border: "1px solid var(--border)",
    borderRadius: 8,
    padding: 12,
    color: "var(--text)",
    fontSize: 14,
    lineHeight: 1.5,
    outline: "none",
    resize: "none",
    fontFamily: "inherit",
  },
  dialogFooter: {
    display: "flex",
    justifyContent: "flex-end",
  },
  dialogConfirmBtn: {
    background: "var(--red, #FF8A7A)",
    color: "#0D0B08",
    border: 0,
    borderRadius: 6,
    padding: "10px 20px",
    fontSize: 12,
    fontWeight: "bold",
    cursor: "pointer",
    textTransform: "uppercase",
    letterSpacing: "0.4px",
  },
  errorBanner: {
    background: "rgba(224, 80, 64, 0.12)",
    border: "1px solid rgba(224, 80, 64, 0.4)",
    color: "var(--red, #FF8A7A)",
    borderRadius: 8,
    padding: "12px 16px",
    marginBottom: 20,
    fontSize: 13,
  },
};
